import React, { forwardRef } from 'react';

import { FieldRenderProps } from 'react-final-form';
import styled from '@emotion/styled';
import { LucidTheme } from '@lucid/theme';

import FieldWrapper from '../field-wrapper/field-wrapper';
import Text from '../../text/text';

interface StyledCheckboxProps {
  /** Checks if the field has an error message. */
  hasError?: boolean;
  /** Checks if the checkbox field has instructions text. */
  hasInstructions?: boolean;
}

export interface CheckboxProps extends FieldRenderProps<string | string[] | number, HTMLInputElement> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The instructions for the checkbox. */
  instructions?: string;
  /** Checks if the field is disabled. */
  isDisabled?: boolean;
  /** Checks if the field is hidden. */
  isHidden?: boolean;
  /** Checks if the field is required to submit. */
  isRequired?: boolean;
}

const StyledCheckbox = styled.input<StyledCheckboxProps>(
  ({
    disabled,
    hasError,
    theme,
    required
  }) => [
    {
      label: 'checkbox',
      outlineOffset: 0,
      transition: 'background-color 0.2s ease-out',
      '&:focus': {
        border: `2px solid ${theme.colors.coolGrey[100]}`
      },
      '&:checked': {
        outline: 'none'
      }
    },
    required && hasError && {
      border: `1px solid ${theme.colors.pink[60]}`
    },
    disabled && {
      pointerEvents: 'none',
      border: `1px solid ${theme.colors.coolGrey[20]}`,
      '&:active': {
        border: `1px solid ${theme.colors.coolGrey[20]}`
      }
    }
  ]);

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>((
  {
    className,
    customErrorMessage,
    id,
    input,
    instructions,
    isDisabled = false,
    isFullWidth = false,
    isHidden = false,
    isRequired = false,
    label,
    meta,
    type = 'checkbox',
    ...rest
  },
  forwardedRef
) => {
  const hasError = meta?.error && meta?.touched;
  const errorMessage = customErrorMessage || meta?.error;
  const hasInstructions = !!instructions;

  return (
    <FieldWrapper
      isCheckbox
      className={className}
      customErrorMessage={errorMessage}
      hasError={hasError}
      hasInstructions={hasInstructions}
      id={id}
      isDisabled={isDisabled}
      isFullWidth={isFullWidth}
      isHidden={isHidden}
      isRequired={isRequired}
      label={label}
      labelVariant="headlineS"
      meta={meta}
    >
      <StyledCheckbox
        {...input}
        {...rest}
        ref={forwardedRef}
        css={(theme: LucidTheme) => ({
          order: 1,
          marginRight: theme.space.s24
        })}
        data-testid={`formCheckbox-${id}`}
        disabled={isDisabled}
        hasError={hasError}
        id={id}
        required={isRequired}
        tabIndex={0}
        type={type}
      />
      {hasInstructions && (
        <Text
          css={(theme: LucidTheme) => ({
            order: 3,
            marginTop: 0,
            marginLeft: theme.space.s40,
            width: '100%'
          })}
          // eslint-disable-next-line react/jsx-sort-props
          className="checkboxInstructions"
          variant="paragraphS400"
        >
          {instructions}
        </Text>
      )}
    </FieldWrapper>
  );
});

export { Checkbox };
export default Checkbox;
